<template>
  <v-dialog v-model="show" max-width="600" class="px-3 pt-6">
    <v-card>
      <v-card-title
        class="body-1"
        style="background-color: #000; color: white;"
      >
        {{ $vuetify.lang.t('$vuetify.EDIT_SESSION') }}
      </v-card-title>
      <v-card-text>
        <v-row>
          <!-- licensePlate -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="licensePlate"
              :label="$vuetify.lang.t('$vuetify.BOOKING.CAR_NUMBER')"
              placeholder="123ABC"
              outlined
              hide-details
              :rules="licensePlateRules"
              required
              autocomplete="off"
              clearable
              @blur="checkIfVehicleIsParking"
            ></v-text-field>
          </v-col>

          <!-- cost in euros -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="costInEuros"
              :label="$vuetify.lang.t('$vuetify.COST_IN_EUROS')"
              placeholder="0.00"
              hide-details
              outlined
              type="number"
              min="0"
              :disabled="
                session.ComputeCostAutomatically || computeCostAutomatically
              "
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- start time date -->
        <v-row>
          <v-col cols="12" md="6">
            <v-menu
              v-if="$vuetify.breakpoint.mdAndUp"
              v-model="startTimeDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedStartTimeDatePart"
                  :value="startTimeDatePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.DATE_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :hide-details="hideStartDateFormatError"
                  :rules="dateFormatRule"
                  autocomplete="off"
                  @click:clear="
                    startTimeDatePart = null
                    formattedStartTimeDatePart = ''
                  "
                  @input="onStartDateInput"
                  @blur="onStartDateBlur"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startTimeDatePart"
                :value="startTimeDatePart"
                :first-day-of-week="1"
                @input="startTimeDatePicker = false"
              ></v-date-picker>
            </v-menu>
            <v-dialog
              v-if="$vuetify.breakpoint.smAndDown"
              v-model="startTimeDatePicker"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedStartTimeDatePart"
                  :value="startTimeDatePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.DATE_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :hide-details="hideStartDateFormatError"
                  :rules="dateFormatRule"
                  autocomplete="off"
                  @click:clear="
                    startTimeDatePart = null
                    formattedStartTimeDatePart = ''
                  "
                  @input="onStartDateInput"
                  @blur="onStartDateBlur"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startTimeDatePart"
                :value="startTimeDatePart"
                :first-day-of-week="1"
                @input="startTimeDatePicker = false"
              ></v-date-picker>
            </v-dialog>
          </v-col>

          <!-- start time time -->
          <v-col cols="12" md="6">
            <v-menu
              v-if="$vuetify.breakpoint.mdAndUp"
              v-model="startTimeTimePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startTimeTimePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.START_TIME')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.TIME_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :rules="timeFormatRule"
                  :hide-details="hideStartTimeFormatError"
                  autocomplete="off"
                  @click:clear="startTimeTimePart = ''"
                  v-on="on"
                  @input="onStartTimeInput"
                  @blur="onStartTimeBlur"
                ></v-text-field>
              </template>
              <v-time-picker
                ref="startTimeTimePicker"
                v-model="startTimeTimePart"
                format="24hr"
              >
                <template v-slot:default>
                  <div class="d-flex justify-end">
                    <v-btn
                      text
                      color="primary" class="secondary--text"
                      @click="cancelStartTimeTimePicker"
                    >
                      {{ $vuetify.lang.t('$vuetify.CANCEL') }}
                    </v-btn>
                    <v-btn color="primary" class="secondary--text" @click="startTimeTimePicker = false">
                      OK
                    </v-btn>
                  </div>
                </template>
              </v-time-picker>
            </v-menu>
            <v-dialog
              v-if="$vuetify.breakpoint.smAndDown"
              v-model="startTimeTimePicker"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startTimeTimePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.START_TIME')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.TIME_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :rules="timeFormatRule"
                  :hide-details="hideStartTimeFormatError"
                  autocomplete="off"
                  @click:clear="startTimeTimePart = ''"
                  v-on="on"
                  @input="onStartTimeInput"
                  @blur="onStartTimeBlur"
                ></v-text-field>
              </template>
              <v-time-picker
                ref="startTimeTimePicker"
                v-model="startTimeTimePart"
                format="24hr"
              >
                <template v-slot:default>
                  <div class="d-flex justify-end">
                    <v-btn
                      text
                      color="primary" class="secondary--text"
                      @click="cancelStartTimeTimePicker"
                    >
                      {{ $vuetify.lang.t('$vuetify.CANCEL') }}
                    </v-btn>
                    <v-btn color="primary" class="secondary--text" @click="startTimeTimePicker = false">
                      OK
                    </v-btn>
                  </div>
                </template>
              </v-time-picker>
            </v-dialog>
          </v-col>
        </v-row>

        <!-- end time date -->
        <v-row>
          <v-col cols="12" md="6">
            <v-menu
              v-if="$vuetify.breakpoint.mdAndUp"
              v-model="endTimeDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedEndTimeDatePart"
                  :value="endTimeDatePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.END_DATE')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.DATE_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :hide-details="hideEndDateFormatError"
                  :rules="dateFormatRule"
                  autocomplete="off"
                  @click:clear="
                    endTimeDatePart = null
                    formattedEndTimeDatePart = ''
                  "
                  @input="onEndDateInput"
                  @blur="onEndDateBlur"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endTimeDatePart"
                :value="endTimeDatePart"
                :first-day-of-week="1"
                @input="endTimeDatePicker = false"
              ></v-date-picker>
            </v-menu>
            <v-dialog
              v-if="$vuetify.breakpoint.smAndDown"
              v-model="endTimeDatePicker"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedEndTimeDatePart"
                  :value="endTimeDatePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.END_DATE')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.DATE_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :hide-details="hideEndDateFormatError"
                  :rules="dateFormatRule"
                  autocomplete="off"
                  @click:clear="
                    endTimeDatePart = null
                    formattedEndTimeDatePart = ''
                  "
                  @input="onEndDateInput"
                  @blur="onEndDateBlur"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="endTimeDatePart"
                :value="endTimeDatePart"
                :first-day-of-week="1"
                @input="endTimeDatePicker = false"
              ></v-date-picker>
            </v-dialog>
            <v-btn text small color="accent" @click="setEndTimeAsNow()">{{
              $vuetify.lang.t('$vuetify.SET_CURRENT_END_TIMESTAMP')
            }}</v-btn>
          </v-col>

          <!-- end time time -->
          <v-col cols="12" md="6">
            <v-menu
              v-if="$vuetify.breakpoint.mdAndUp"
              v-model="endTimeTimePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endTimeTimePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.END_TIME')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.TIME_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :rules="timeFormatRule"
                  :hide-details="hideEndTimeFormatError"
                  autocomplete="off"
                  @click:clear="endTimeTimePart = ''"
                  v-on="on"
                  @input="onEndTimeInput"
                  @blur="onEndTimeBlur"
                ></v-text-field>
              </template>
              <v-time-picker
                ref="endTimeTimePicker"
                v-model="endTimeTimePart"
                format="24hr"
              >
                <template v-slot:default>
                  <div class="d-flex justify-end">
                    <v-btn
                      text
                      color="primary" class="secondary--text"
                      @click="cancelEndTimeTimePicker"
                    >
                      {{ $vuetify.lang.t('$vuetify.CANCEL') }}
                    </v-btn>
                    <v-btn color="primary" class="secondary--text" @click="endTimeTimePicker = false">
                      OK
                    </v-btn>
                  </div>
                </template>
              </v-time-picker>
            </v-menu>
            <v-dialog
              v-if="$vuetify.breakpoint.smAndDown"
              v-model="endTimeTimePicker"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endTimeTimePart"
                  :label="$vuetify.lang.t('$vuetify.BOOKING.END_TIME')"
                  :placeholder="
                    $vuetify.lang.t('$vuetify.BOOKING.TIME_PLACEHOLDER')
                  "
                  outlined
                  clearable
                  v-bind="attrs"
                  :rules="timeFormatRule"
                  :hide-details="hideEndTimeFormatError"
                  autocomplete="off"
                  @click:clear="endTimeTimePart = ''"
                  v-on="on"
                  @input="onEndTimeInput"
                  @blur="onEndTimeBlur"
                ></v-text-field>
              </template>
              <!-- Time picker -->
              <v-time-picker
                ref="endTimeTimePicker"
                v-model="endTimeTimePart"
                format="24hr"
              >
                <template v-slot:default>
                  <div class="d-flex justify-end">
                    <v-btn
                      text
                      color="primary" class="secondary--text"
                      @click="cancelEndTimeTimePicker"
                    >
                      {{ $vuetify.lang.t('$vuetify.CANCEL') }}
                    </v-btn>
                    <v-btn color="primary" class="secondary--text" @click="endTimeTimePicker = false">
                      OK
                    </v-btn>
                  </div>
                </template>
              </v-time-picker>
            </v-dialog>
          </v-col>
        </v-row>

        <v-checkbox
          v-model="computeCostAutomatically"
          :label="$vuetify.lang.t('$vuetify.AUTOMATIC_PAY_AFTER_SAVE')"
        ></v-checkbox>
        <v-checkbox
          v-model="chargeForSession"
          :label="$vuetify.lang.t('$vuetify.MAKE_PAYMENT_AFTER_SAVE')"
        ></v-checkbox>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="accent" text @click="show = false">
          {{ $vuetify.lang.t('$vuetify.CANCEL') }}
        </v-btn>
        <v-btn
          color="primary"
          class="secondary--text"
          :disabled="saveButtonDisabled"
          @click="saveSessionInfo()"
        >
          {{ $vuetify.lang.t('$vuetify.SAVE') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'EditSessionDialog',
  props: {
    session: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data() {
    return {
      startTimeDatePicker: false,
      startTimeTimePicker: false,
      startTimeDatePart: null,
      startTimeTimePart: '',
      formattedStartTimeDatePart: '',
      startTime: '',

      endTimeDatePicker: false,
      endTimeTimePicker: false,
      endTimeDatePart: null,
      endTimeTimePart: '',
      formattedEndTimeDatePart: '',
      endTimePicker: false,
      endTime: '',

      computeCostAutomatically: false,
      chargeForSession: false,
      costInCents: 0,
      costInEuros: '',

      licensePlate: '',

      dateFormatRule: [
        v => {
          if (!v) return true
          return (
            this.isDateValid(v) || this.$vuetify.lang.t('$vuetify.INVALID_DATE')
          )
        }
      ],
      timeFormatRule: [
        v =>
          !v ||
          this.isTimeValid(v) ||
          this.$vuetify.lang.t('$vuetify.INVALID_TIME')
      ],
      licensePlateRules: [v => !!v || this.$vuetify.lang.t('$vuetify.REQUIRED')]
    }
  },
  computed: {
    ...mapGetters('app', ['loading']),
    show: {
      get() {
        return this.visible
      },
      set(value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    hideStartDateFormatError() {
      const v = this.formattedStartTimeDatePart
      return !v || this.isDateValid(v)
    },
    hideStartTimeFormatError() {
      const v = this.startTimeTimePart
      return !v || this.isTimeValid(v)
    },
    hideEndDateFormatError() {
      const v = this.formattedEndTimeDatePart
      return !v || this.isDateValid(v)
    },
    hideEndTimeFormatError() {
      const v = this.endTimeTimePart
      return !v || this.isTimeValid(v)
    },

    isStartDateValid() {
      const v = this.formattedStartTimeDatePart || this.startTimeDatePart
      return this.isDateValid(v)
    },
    isStartTimeValid() {
      return this.isTimeValid(this.startTimeTimePart)
    },
    isEndDateValid() {
      const hasAny =
        !!this.formattedEndTimeDatePart ||
        !!this.endTimeDatePart ||
        !!this.endTimeTimePart
      if (!hasAny) return true
      const v = this.formattedEndTimeDatePart || this.endTimeDatePart
      return this.isDateValid(v)
    },
    isEndTimeValid() {
      const hasAny =
        !!this.formattedEndTimeDatePart ||
        !!this.endTimeDatePart ||
        !!this.endTimeTimePart
      if (!hasAny) return true
      return this.isTimeValid(this.endTimeTimePart)
    },

    saveButtonDisabled() {
      const lpOk = !!this.licensePlate
      const startOk = this.isStartDateValid && this.isStartTimeValid
      const endOk = this.isEndDateValid && this.isEndTimeValid
      return !(lpOk && startOk && endOk)
    }
  },
  watch: {
    startTimeDatePart(val) {
      this.formattedStartTimeDatePart = val ? this.datePickerFormat(val) : ''
    },
    endTimeDatePart(val) {
      this.formattedEndTimeDatePart = val ? this.datePickerFormat(val) : ''
    }
  },
  created() {
    this.licensePlate =
      this.session.CarNumber || this.session.LicensePlate || ''
    this.costInCents =
      typeof this.session.CostInCents === 'number'
        ? this.session.CostInCents
        : 0
    this.costInEuros =
      typeof this.costInCents === 'number'
        ? (this.costInCents / 100).toFixed(2)
        : ''
    this.computeCostAutomatically = !!this.session.ComputeCostAutomatically
    this.chargeForSession = !!this.session.ChargeForSession

    let startSource
    if (this.session && this.session.StartTime) {
      startSource = new Date(this.session.StartTime)
    } else if (this.startTime) {
      startSource = new Date(this.startTime)
    } else {
      startSource = new Date()
    }
    this.startTime = startSource
    {
      const yyyy = String(startSource.getFullYear())
      const mm = String(startSource.getMonth() + 1).padStart(2, '0')
      const dd = String(startSource.getDate()).padStart(2, '0')
      const HH = String(startSource.getHours()).padStart(2, '0')
      const MM = String(startSource.getMinutes()).padStart(2, '0')
      this.startTimeDatePart = `${yyyy}-${mm}-${dd}`
      this.formattedStartTimeDatePart = this.datePickerFormat(startSource)
      this.startTimeTimePart = `${HH}:${MM}`
    }

    let sourceDate
    if (this.session && this.session.EndTime) {
      sourceDate = new Date(this.session.EndTime)
    } else if (this.endTime) {
      sourceDate = new Date(this.endTime)
    } else {
      sourceDate = new Date()
    }
    this.endTime = sourceDate
    {
      const yyyy = String(sourceDate.getFullYear())
      const mm = String(sourceDate.getMonth() + 1).padStart(2, '0')
      const dd = String(sourceDate.getDate()).padStart(2, '0')
      const HH = String(sourceDate.getHours()).padStart(2, '0')
      const MM = String(sourceDate.getMinutes()).padStart(2, '0')
      this.endTimeDatePart = `${yyyy}-${mm}-${dd}`
      this.formattedEndTimeDatePart = this.datePickerFormat(sourceDate)
      this.endTimeTimePart = `${HH}:${MM}`
    }
  },
  methods: {
    datePickerFormat(dateValue) {
      const date = new Date(dateValue)
      const dd = String(date.getDate()).padStart(2, '0')
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const yyyy = String(date.getFullYear())
      return `${dd}-${mm}-${yyyy}`
    },

    // ----- Validation helpers -----
    isDateValid(value) {
      if (!value) return false
      const normalized = this.normalizeDateString(value)
      return (
        /^\d{4}-\d{2}-\d{2}$/.test(normalized) ||
        /^\d{2}-\d{2}-\d{4}$/.test(normalized)
      )
    },
    isTimeValid(value) {
      if (!value) return false
      return /^\d{2}:\d{2}$/.test(value)
    },

    // ----- Date normalization -----
    normalizeDateString(value) {
      if (!value) return ''
      return String(value)
        .replace(/[.,\s]+/g, '-')
        .replace(/-+/g, '-')
        .trim()
    },

    updateInternalDateFromFormatted(formattedProp, internalProp) {
      const raw = this[formattedProp]
      if (!raw) {
        this[internalProp] = null
        return
      }

      const cleaned = this.normalizeDateString(raw)
      if (cleaned !== raw) {
        this[formattedProp] = cleaned
      }

      const parts = cleaned.split('-')
      if (parts.length !== 3) return

      const first = parts[0]
      const second = parts[1]
      const third = parts[2]

      let yyyy
      let mm
      let dd

      if (/^\d{4}$/.test(first)) {
        // yyyy-mm-dd
        yyyy = first
        mm = second
        dd = third
      } else {
        // dd-mm-yyyy
        dd = first
        mm = second
        yyyy = third
      }

      const iso = `${String(yyyy)}-${String(mm).padStart(2, '0')}-${String(
        dd
      ).padStart(2, '0')}`

      this[internalProp] = iso
    },

    onStartDateInput(value) {
      const normalized = this.normalizeDateString(value)
      if (normalized !== value) {
        this.formattedStartTimeDatePart = normalized
      }
    },

    onEndDateInput(value) {
      const normalized = this.normalizeDateString(value)
      if (normalized !== value) {
        this.formattedEndTimeDatePart = normalized
      }
    },

    onStartDateBlur() {
      this.updateInternalDateFromFormatted(
        'formattedStartTimeDatePart',
        'startTimeDatePart'
      )
    },

    onEndDateBlur() {
      this.updateInternalDateFromFormatted(
        'formattedEndTimeDatePart',
        'endTimeDatePart'
      )
    },

    // ----- Time normalization -----
    normalizeTimeChars(value) {
      if (!value) return ''
      const str = String(value)
      const noSpaces = str.replace(/\s+/g, '')
      const replaced = noSpaces.replace(/[.,]/g, ':')
      return replaced
    },

    normalizeTimeString(value) {
      if (!value) return ''
      const prepared = this.normalizeTimeChars(value)
      if (!prepared) return ''

      if (!prepared.includes(':')) {
        const digitsOnly = prepared.replace(/\D/g, '')
        if (!digitsOnly) {
          return ''
        }

        const { length } = digitsOnly
        let hoursSource
        let minutesSource

        if (length === 1 || length === 2) {
          hoursSource = digitsOnly
          minutesSource = '0'
        } else if (length === 3) {
          hoursSource = digitsOnly.slice(0, 1)
          minutesSource = digitsOnly.slice(1)
        } else {
          hoursSource = digitsOnly.slice(0, length - 2)
          minutesSource = digitsOnly.slice(length - 2)
        }

        const hoursNumber = parseInt(hoursSource, 10)
        const minutesNumber = parseInt(minutesSource, 10)

        if (Number.isNaN(hoursNumber) || Number.isNaN(minutesNumber)) {
          return prepared
        }

        const hoursPadded = String(hoursNumber).padStart(2, '0')
        const minutesPadded = String(minutesNumber).padStart(2, '0')
        return `${hoursPadded}:${minutesPadded}`
      }

      const parts = prepared.split(':')
      const first = parts[0]
      const second = parts[1]

      const hoursNumber = parseInt(first, 10)
      const minutesNumber = parseInt(second, 10)

      if (Number.isNaN(hoursNumber) || Number.isNaN(minutesNumber)) {
        return prepared
      }

      const hoursPadded = String(hoursNumber).padStart(2, '0')
      const minutesPadded = String(minutesNumber).padStart(2, '0')
      return `${hoursPadded}:${minutesPadded}`
    },

    onStartTimeInput(value) {
      const normalized = this.normalizeTimeChars(value)
      if (normalized !== value) {
        this.startTimeTimePart = normalized
      }
    },

    onEndTimeInput(value) {
      const normalized = this.normalizeTimeChars(value)
      if (normalized !== value) {
        this.endTimeTimePart = normalized
      }
    },

    onStartTimeBlur() {
      const normalized = this.normalizeTimeString(this.startTimeTimePart)
      if (normalized !== this.startTimeTimePart) {
        this.startTimeTimePart = normalized
      }
      if (typeof this.updatePriceInfo === 'function') {
        this.updatePriceInfo()
      }
    },

    onEndTimeBlur() {
      const normalized = this.normalizeTimeString(this.endTimeTimePart)
      if (normalized !== this.endTimeTimePart) {
        this.endTimeTimePart = normalized
      }
      if (typeof this.updatePriceInfo === 'function') {
        this.updatePriceInfo()
      }
    },

    // ----- Date building for save -----
    buildDate(dateStr, timeStr) {
      if (!dateStr || !timeStr) return null

      const normalizedDateStr = String(dateStr)
        .replace(/[.,\s]+/g, '-')
        .trim()

      const parts = normalizedDateStr.split('-').map(n => parseInt(n, 10))

      const p1 = parts[0]
      const p2 = parts[1]
      const p3 = parts[2]

      let y
      let m
      let d

      if (/^\d{4}/.test(normalizedDateStr)) {
        y = p1
        m = p2
        d = p3
      } else {
        d = p1
        m = p2
        y = p3
      }

      const timeParts = String(timeStr)
        .split(':')
        .map(n => parseInt(n, 10))
      const hh = timeParts[0]
      const mm = timeParts[1]

      return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0)
    },

    async saveSessionInfo() {
      const startDate =
        this.startTimeDatePart && this.startTimeTimePart
          ? this.buildDate(this.startTimeDatePart, this.startTimeTimePart)
          : null

      const endDate =
        this.endTimeDatePart && this.endTimeTimePart
          ? this.buildDate(this.endTimeDatePart, this.endTimeTimePart)
          : null

      const startISO = startDate ? startDate.toISOString() : null
      const endISO = endDate ? endDate.toISOString() : null

      let { costInCents } = this

      if (
        !this.computeCostAutomatically &&
        !this.session.ComputeCostAutomatically &&
        this.costInEuros !== null &&
        this.costInEuros !== undefined &&
        this.costInEuros !== ''
      ) {
        const normalized = String(this.costInEuros)
          .replace(',', '.')
          .trim()
        const parsed = parseFloat(normalized)
        if (!Number.isNaN(parsed) && parsed >= 0) {
          costInCents = Math.round(parsed * 100)
        }
      }
      this.costInCents = costInCents

      await this.$store.dispatch('sessions/modifySession', {
        ParkingSessionId: this.session.Id,
        ...(startISO ? { StartTime: startISO } : {}),
        ...(endISO ? { EndTime: endISO } : {}),
        CarNumber: this.licensePlate,
        CostInCents: this.costInCents,
        ComputeCostAutomatically: this.computeCostAutomatically,
        ChargeForSession: this.chargeForSession
      })

      this.$store.commit('sessions/setSessionKeyValueInSessionsArray', {
        SessionId: this.session.Id,
        key: 'StartTime',
        value: startISO
      })
      if (endISO) {
        this.$store.commit('sessions/setSessionKeyValueInSessionsArray', {
          SessionId: this.session.Id,
          key: 'EndTime',
          value: endISO
        })
      }
      this.$store.commit('sessions/setSessionKeyValueInSessionsArray', {
        SessionId: this.session.Id,
        key: 'CarNumber',
        value: this.licensePlate
      })
      this.$store.commit('sessions/setSessionKeyValueInSessionsArray', {
        SessionId: this.session.Id,
        key: 'CostInCents',
        value: this.costInCents
      })

      this.show = false
    },

    setEndTimeAsNow() {
      const now = new Date()
      const yyyy = String(now.getFullYear())
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      const HH = String(now.getHours()).padStart(2, '0')
      const MM = String(now.getMinutes()).padStart(2, '0')
      this.endTimeDatePart = `${yyyy}-${mm}-${dd}`
      this.formattedEndTimeDatePart = this.datePickerFormat(now)
      this.endTimeTimePart = `${HH}:${MM}`
    },

    cancelStartTimeTimePicker() {
      this.startTimeTimePicker = false
    },
    cancelEndTimeTimePicker() {
      this.endTimeTimePicker = false
    }
  }
}
</script>
